import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { Button, Tooltip, message as antdMessage } from "antd";
import {
  SendOutlined,
  LoadingOutlined,
  CopyOutlined,
  CheckOutlined,
  EditOutlined,
  CloseOutlined,
  CloseCircleOutlined,
  HistoryOutlined,
} from "@ant-design/icons";
import { WandSparkles, SquarePen, Mic, Square, Sparkles } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import VoiceLevelBars from "@/components/VoiceLevelBars";
import AIProcessingButtonFrame from "@/components/ui/AIProcessingButtonFrame";
import ChatModeToggle from "@/components/ChatModeToggle";
import DeepThinkingIndicator from "@/components/DeepThinkingIndicator";
import LongRunningIndicator from "@/components/LongRunningIndicator";
import ChatModelSelector from "@/components/ChatModelSelector";
import ChatTagSelector from "@/components/ChatTagSelector";
import ChatHistoryDrawer from "@/components/ChatHistoryDrawer";
import ChatGroundingDrawer from "@/components/ChatGroundingDrawer";
import { useVoiceChatMode } from "@/hooks/useVoiceChatMode";
import { useVoiceDictation } from "@/hooks/useVoiceDictation";
import { preprocessGroundingMarkerRuns } from "@/utility/groundingMarkers";
import { getGroundingMarkdownComponents } from "@/components/ui/GroundingMarkdownLink";
import { HIDDEN_PROMPT_SUFFIX_MARKER } from "@/utility/chatHelpers";

import { useEnvironment } from "@/context/EnvironmentContext";
import { useAuth } from "@/context/AuthContext";
import Logger from "@/utility/logger";
import { defaultChatMessage } from "@/lib/defaults";
import type {
  ChatMessage,
  ChatHistory,
  ThinkMode,
  AgentStreamUpdate,
  ModelEntry,
  Tag,
} from "@/lib/types";
import {
  chatThreadToMessages,
  getStoredLastChatId,
  storeLastChatId,
  clearStoredLastChatId,
} from "@/utility/chatSessionPersistence";
import { appendLogToMessage, patchMessageById } from "@/utility/chatMessageUpdater";

const AGENTIC_PROMPT_TEMPLATE_NAME = "Default Agentic Prompt";

interface ChatProps {
  active: boolean;
  onSendMessage?: (
    message: string,
    options?: {
      llm_name?: string;
      include_tag_ids?: string[];
      exclude_tag_ids?: string[];
    }
  ) => Promise<{ answer: string; id?: string; sources?: any[] }>;
  onNewChat?: () => void;
  placeholder?: string;
  messages: ChatMessage[];
  setMessages: React.Dispatch<React.SetStateAction<ChatMessage[]>>;
  messageChatId?: string | null;
  setMessageChatId?: (id: string | null) => void;
  selectedProjectId?: string;
  selectedReportTemplateId?: string;
  location: string;
  transcribeAudio: (audioBlob: Blob) => Promise<string>;
  synthesizeSpeech?: (text: string, voice?: string) => Promise<Blob>;
  fetchSettingsGroup: (report_template_id: string) => any;
  reportTemplateId: string;
  handleSectionGeneration: any;
  setSelectedSections: any;
  generatedSections: any;
  selectedSections: any;
  logAuditAction?: (id: string, action: "CREATE" | "UPDATE" | "DELETE", section_name?: string) => Promise<void>;
  reportTemplateProjectId?: string | null;
  // Backend APIs
  getProjectChatHistory?: (projectId: string, options?: any) => Promise<any>;
  getChatThread?: (chatHistoryId: string) => Promise<any>;
  setChatPinned?: (chatHistoryId: string, isPinned: boolean) => Promise<any>;
  renameChatHistory?: (chatHistoryId: string, title: string) => Promise<any>;
  deleteChatHistory?: (chatHistoryId: string) => Promise<void>;
  startAgentQuery?: (projectId: string, payload: any) => Promise<any>;
  getAgentTaskStatus?: (taskId: string) => Promise<any>;
  streamAgentTaskStatus?: (
    taskId: string,
    onMessage: (data: any) => void,
    onError: (err: any) => void,
    onClose: (intentional: boolean) => void
  ) => AbortController;
  getModels?: (agentic?: boolean) => Promise<any>;
  getProjectTags?: (projectId: string) => Promise<any>;
  getPromptTemplates?: (perPage?: number) => Promise<any>;
  retrieveAgentGrounding?: (taskId: string) => Promise<any>;
  retrieveSourceDocument?: (documentId: string) => Promise<any>;
}

const EMPTY_ANSWER =
  "No answer came back for that question. Try rephrasing it, or ask again in a moment.";

const ChatPage: React.FC<ChatProps> = ({
  active,
  onSendMessage,
  onNewChat,
  placeholder = "Ask me anything about your document...",
  messages,
  setMessages,
  messageChatId,
  setMessageChatId,
  selectedProjectId,
  selectedReportTemplateId,
  location,
  transcribeAudio,
  synthesizeSpeech,
  fetchSettingsGroup,
  reportTemplateId,
  handleSectionGeneration,
  setSelectedSections,
  generatedSections,
  selectedSections,
  logAuditAction,
  reportTemplateProjectId,
  getProjectChatHistory,
  getChatThread,
  setChatPinned,
  renameChatHistory,
  deleteChatHistory,
  startAgentQuery,
  getAgentTaskStatus,
  streamAgentTaskStatus,
  getModels,
  getProjectTags,
  getPromptTemplates,
  retrieveAgentGrounding,
  retrieveSourceDocument,
}) => {
  const { selectedEnvironment } = useEnvironment();
  const { isSuperAdmin, organizationId } = useAuth();

  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null);
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [editingContent, setEditingContent] = useState("");

  // Feature Modes & Management States
  const [thinkMode, setThinkMode] = useState<ThinkMode>("lite");
  const [showHistoryDrawer, setShowHistoryDrawer] = useState(false);

  // Grounding Evidence Drawer
  const [groundingDrawerTaskId, setGroundingDrawerTaskId] = useState<string | null>(null);
  const [groundingDrawerTarget, setGroundingDrawerTarget] = useState<{
    kind: "source" | "rationale";
    id: string;
  } | null>(null);

  const handleOpenGrounding = (
    taskId?: string | null,
    target?: { kind: "source" | "rationale"; id: string } | null
  ) => {
    if (!taskId) return;
    setGroundingDrawerTaskId(taskId);
    setGroundingDrawerTarget(target || null);
  };

  const activePlaceholder = !selectedProjectId
    ? "Select a project first…"
    : placeholder !== "Ask me anything about your document..."
    ? placeholder
    : "Ask a question about your project documents…";

  const prevProjectIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!selectedProjectId) return;
    if (prevProjectIdRef.current !== null && prevProjectIdRef.current !== selectedProjectId) {
      setMessages(defaultChatMessage);
      if (setMessageChatId) setMessageChatId(null);
      setIncludeTagIds([]);
      setExcludeTagIds([]);
    }
    prevProjectIdRef.current = selectedProjectId;
  }, [selectedProjectId, setMessages, setMessageChatId]);

  // Model & Tag Selection (Super Admin)
  const [models, setModels] = useState<ModelEntry[]>([]);
  const [selectedModel, setSelectedModel] = useState<string>("");
  const [showModelMenu, setShowModelMenu] = useState(false);

  const [tags, setTags] = useState<Tag[]>([]);
  const [includeTagIds, setIncludeTagIds] = useState<string[]>([]);
  const [excludeTagIds, setExcludeTagIds] = useState<string[]>([]);
  const [showTagMenu, setShowTagMenu] = useState(false);

  // Agent Template Resolution
  const [agenticPromptTemplateId, setAgenticPromptTemplateId] = useState<string | null>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const sessionRef = useRef(0);
  const activeStreamControllerRef = useRef<AbortController | null>(null);

  const [sectionGenerationByCommand, setSectionGenerationByCommand] = useState(false);
  const advancedAIChatUrl = selectedEnvironment?.workspace_url
    ? `${selectedEnvironment.workspace_url}/app-0001/chat`
    : null;

  // Preserve memory across tab changes: only reset editing transient UI
  useEffect(() => {
    if (active) return;
    setEditingMessageId(null);
    setEditingContent("");
  }, [active]);

  useEffect(() => {
    const handleAsyncSectionGeneration = async () => {
      await handleSectionGeneration(false);
      setSectionGenerationByCommand(false);
    };

    if (selectedSections.length > 0 && sectionGenerationByCommand === true) {
      handleAsyncSectionGeneration();
    }
  }, [selectedSections, sectionGenerationByCommand, handleSectionGeneration]);

  // Load Models & Tags for Super Admins
  useEffect(() => {
    if (!isSuperAdmin) return undefined;

    let cancelled = false;
    if (getModels) {
      getModels(false)
        .then((data) => {
          if (!cancelled && Array.isArray(data)) {
            setModels(data);
            if (data.length > 0 && !selectedModel) {
              setSelectedModel(data[0].id);
            }
          }
        })
        .catch(() => {});
    }

    if (getProjectTags && selectedProjectId) {
      getProjectTags(selectedProjectId)
        .then((data) => {
          if (!cancelled && data?.items) {
            setTags(data.items);
          }
        })
        .catch(() => {});
    }

    return () => {
      cancelled = true;
    };
  }, [isSuperAdmin, selectedProjectId, getModels, getProjectTags]);

  // Resolve Agentic Prompt Template for Deep Thinking
  useEffect(() => {
    if (!getPromptTemplates) return undefined;
    let cancelled = false;

    getPromptTemplates(100)
      .then((data) => {
        if (cancelled) return;
        const list = Array.isArray(data) ? data : data?.items || [];
        const found = list.find(
          (t: any) =>
            t.name?.toLowerCase().trim() === AGENTIC_PROMPT_TEMPLATE_NAME.toLowerCase().trim()
        );
        if (found) {
          setAgenticPromptTemplateId(found.id);
          Logger.debug("ChatPage: Resolved agentic prompt template ID:", found.id);
        } else {
          Logger.warn(
            "ChatPage: Agentic prompt template not found among:",
            list.map((t: any) => t.name)
          );
        }
      })
      .catch((err) => {
        Logger.error("ChatPage: Failed to fetch prompt templates:", err);
      });

    return () => {
      cancelled = true;
    };
  }, [getPromptTemplates]);

  // Scroll to bottom when messages update
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isLoading]);

  const handleToggleTag = (tagId: string, type: "include" | "exclude") => {
    if (type === "include") {
      setIncludeTagIds((prev) =>
        prev.includes(tagId) ? prev.filter((id) => id !== tagId) : [...prev, tagId]
      );
    } else {
      setExcludeTagIds((prev) =>
        prev.includes(tagId) ? prev.filter((id) => id !== tagId) : [...prev, tagId]
      );
    }
  };

  // ────────────────────────────────────────────
  // Test Command Handler (/jenly)
  // ────────────────────────────────────────────
  const handleTestCommand = async (command: string, sessionId: number) => {
    let responseContent = "";
    const cleanCommand = command.toLowerCase().trim();
    Logger.debug("ChatPage.tsx: Processing test command:", cleanCommand);

    try {
      const settingsGroups = await fetchSettingsGroup(reportTemplateId);

      if (cleanCommand === "/jenly") {
        responseContent = `### All available commands
Select a command to proceed:

* \`/jenly list\` 
* \`/jenly getsection <group_name>\` 
* \`/jenly generate all\`
* \`/jenly generate <group_name>\`
* \`/jenly help\`
`;
      } else if (cleanCommand === "/jenly help") {
        responseContent = `### Available Operations for /jenly
Select a command to proceed:

* \`/jenly list\` : View all available settings groups and their names.
* \`/jenly getsection <settings_group_name>\` : Extract text from the Word document for a specific group.
    * *Example: \`/jenly getsection psur_1_0\`*
* \`/jenly generate all\`  : Generate all of the document sections.
* \`/jenly generate <settings_group_name>\` : Generate a particular section
* \`/jenly help\` : Get detailed instructions about /jenly commands

---
*Note: Ensure your document contains the required START/END bookmarks for the extraction to work.*`;
      } else if (cleanCommand === "/jenly list") {
        const tableRows = [...settingsGroups]
          .sort((a, b) => a.name.localeCompare(b.name))
          .map((g) => `| ${g.name} | ${g.display_name} |`)
          .join("\n");
        responseContent = `### 🛠 Debug: Available Settings Groups\n| Name | Display Name |\n| :--- | :--- |\n${tableRows}`;
      } else if (cleanCommand.startsWith("/jenly getsection")) {
        const requestedGroupName = cleanCommand.replace("/jenly getsection", "").trim();
        const foundGroup = settingsGroups.find((g: any) => g.name.toLowerCase() === requestedGroupName);

        if (!foundGroup) {
          responseContent = `**Error:** Settings group "${requestedGroupName}" not found.`;
        } else {
          await Word.run(async (context) => {
            const startBookmark = `${foundGroup.insertion_key}_START`;
            const endBookmark = `${foundGroup.insertion_key}_END`;

            try {
              const rangeStart = context.document.getBookmarkRange(startBookmark);
              const rangeEnd = context.document.getBookmarkRange(endBookmark);
              const combinedRange = rangeStart.expandTo(rangeEnd);
              combinedRange.load("text");
              await context.sync();

              responseContent = `### 📄 Extracted Content (${foundGroup.display_name})\n\n${combinedRange.text}`;
            } catch (err: any) {
              responseContent = `❌ **Word Error:** Could not read bookmarks (${startBookmark}/${endBookmark}).\n${err.message}`;
            }
          });
        }
      } else if (cleanCommand.startsWith("/jenly generate all")) {
        Logger.debug("ChatPage.tsx: Starting generation for all sections");
        if (logAuditAction && reportTemplateProjectId) {
          logAuditAction(reportTemplateProjectId, "CREATE", "Chat Command: /jenly generate all");
        }
        responseContent = `All the section generation has been started successfully. Please wait until all of the sections are generated successfully.`;

        const assistantMessage: ChatMessage = {
          id: (Date.now() + 1).toString(),
          content: responseContent,
          role: "test",
          timestamp: new Date(),
        };
        if (sessionRef.current === sessionId) {
          setMessages((prev) => [...prev, assistantMessage]);
          setIsLoading(false);
        }

        await handleSectionGeneration(true);
        responseContent = "All the sections have been generated successfully.";
      } else if (cleanCommand.startsWith("/jenly generate")) {
        const requestedGroupName = cleanCommand.replace("/jenly generate", "").trim();
        const foundGroup = settingsGroups.find((g: any) => g.name.toLowerCase() === requestedGroupName);

        if (!foundGroup) {
          responseContent = `**Error:** Settings group "${requestedGroupName}" not found.`;
          if (logAuditAction && reportTemplateProjectId) {
            logAuditAction(reportTemplateProjectId, "CREATE", `Chat Command: /jenly generate ${requestedGroupName} (not found)`);
          }
        } else {
          const isAlreadyGenerated = generatedSections.some((gs: any) => gs.id === foundGroup.id);

          if (isAlreadyGenerated) {
            responseContent = `**Info:** Section "${foundGroup.display_name}" has already been generated.`;
          } else {
            setSelectedSections([foundGroup]);
            setSectionGenerationByCommand(true);

            Logger.debug("ChatPage.tsx: Starting generation for section:", foundGroup.display_name);
            if (logAuditAction && reportTemplateProjectId) {
              logAuditAction(reportTemplateProjectId, "CREATE", `Chat Command: /jenly generate ${foundGroup.display_name}`);
            }
            responseContent = `Starting generation for "${foundGroup.display_name}"...`;
          }
        }
      } else {
        responseContent = "Unknown jenly command. Try `/jenly` or `/jenly getsection [name]` or /jenly generate all";
      }
    } catch (error: any) {
      Logger.error("ChatPage.tsx: Test command error:", error);
      responseContent = `System Error: ${error.message}`;
    }

    const assistantMessage: ChatMessage = {
      id: (Date.now() + 1).toString(),
      content: responseContent,
      role: "test",
      timestamp: new Date(),
    };
    if (sessionRef.current === sessionId) {
      setMessages((prev) => [...prev, assistantMessage]);
      setIsLoading(false);
    }
  };

  // ────────────────────────────────────────────
  // Sending Messages
  // ────────────────────────────────────────────

  const handleSendMessage = async (overrideText?: string) => {
    // Voice mode passes the recognized transcript explicitly; guard against a
    // stray DOM event leaking in as the first argument.
    const sourceText = typeof overrideText === "string" ? overrideText : inputValue;
    if (!sourceText.trim() || isLoading) return;

    const userText = sourceText.trim();
    const sessionId = sessionRef.current;
    const isTestCommand = userText.toLowerCase().startsWith("/jenly");

    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      content: userText,
      role: isTestCommand ? "test" : "user",
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputValue("");
    setIsLoading(true);

    if (isTestCommand) {
      await handleTestCommand(userText, sessionId);
      return;
    }

    if (logAuditAction && reportTemplateProjectId) {
      logAuditAction(reportTemplateProjectId, "CREATE", `Chat: ${userText.substring(0, 100)}`);
    }

    if (thinkMode === "deep") {
      await executeDeepThinking(userText);
    } else {
      await executeLiteChat(userText);
    }
  };

  // Lite Mode Execution (POST /chat)
  const executeLiteChat = async (userText: string) => {
    if (!selectedProjectId) {
      antdMessage.error("Please select a project first");
      setIsLoading(false);
      return;
    }

    const sessionId = sessionRef.current;

    try {
      if (!onSendMessage) throw new Error("Send handler unavailable");

      const conversationContext = messages
        .filter((m) => m.role !== "test" && !m.error)
        .map((m) => `**${m.role === "user" ? "User" : "Assistant"}:** ${m.content}`)
        .join("\n");

      const augmentedQuestion =
        userText +
        `\n\nFormat all responses in markdown format (do not include three backticks followed by 'markdown' at the start of the response).\n\nHere is the context of the conversation:\n\n${conversationContext}`;

      const response = await onSendMessage(augmentedQuestion, {
        llm_name: selectedModel || undefined,
        include_tag_ids: includeTagIds.length ? includeTagIds : undefined,
        exclude_tag_ids: excludeTagIds.length ? excludeTagIds : undefined,
      });

      const rawAnswer = response?.answer || (typeof response === "string" ? response : "");
      const finalAnswer = rawAnswer.trim() ? rawAnswer : EMPTY_ANSWER;
      const assistantMessage: ChatMessage = {
        id: `asst-${Date.now()}`,
        content: finalAnswer,
        role: "assistant",
        timestamp: new Date(),
        sources: response.sources,
      };

      if (sessionRef.current === sessionId) {
        setMessages((prev) => [...prev, assistantMessage]);
      }
    } catch (error) {
      Logger.error("ChatPage: Error sending message:", error);
      const errorMessage: ChatMessage = {
        id: `err-${Date.now()}`,
        content: "I apologize, but I encountered an error. Please try again.",
        role: "assistant",
        timestamp: new Date(),
        error: "Error processing request",
      };
      if (sessionRef.current === sessionId) {
        setMessages((prev) => [...prev, errorMessage]);
      }
    } finally {
      if (sessionRef.current === sessionId) setIsLoading(false);
    }
  };

  // Deep Thinking Mode Execution (POST /agent/query + SSE)
  const executeDeepThinking = async (userText: string) => {
    if (!selectedProjectId) {
      antdMessage.error("Please select a project first");
      setIsLoading(false);
      return;
    }

    let templateId = agenticPromptTemplateId;
    if (!templateId && getPromptTemplates) {
      try {
        const data = await getPromptTemplates(100);
        const list = Array.isArray(data) ? data : data?.items || [];
        const found = list.find(
          (t: any) =>
            t.name?.toLowerCase().trim() === AGENTIC_PROMPT_TEMPLATE_NAME.toLowerCase().trim()
        );
        if (found) {
          templateId = found.id;
          setAgenticPromptTemplateId(found.id);
          Logger.debug("ChatPage: Resolved agentic template ID on-the-fly:", found.id);
        }
      } catch (err) {
        Logger.error("ChatPage: On-the-fly prompt template lookup failed:", err);
      }
    }

    if (!templateId) {
      antdMessage.error(
        `Deep Thinking is unavailable: prompt template "${AGENTIC_PROMPT_TEMPLATE_NAME}" was not found.`
      );
      setIsLoading(false);
      return;
    }

    if (!startAgentQuery || !streamAgentTaskStatus) {
      antdMessage.error("Agent query service is unavailable");
      setIsLoading(false);
      return;
    }

    const placeholderId = `asst-agent-${Date.now()}`;
    const assistantPlaceholder: ChatMessage = {
      id: placeholderId,
      role: "assistant",
      content: "",
      timestamp: new Date(),
      isStreaming: true,
      status: "running",
      logs: [],
    };

    setMessages((prev) => [...prev, assistantPlaceholder]);

    const agentChatHistory = messages
      .filter((m) => m.role !== "test" && !m.error && m.content)
      .map((m) => `**${m.role === "user" ? "User" : "Assistant"}:** ${m.content}`)
      .join("\n");
    const finalAgentPlan = agentChatHistory.length > 0
      ? `${userText}${HIDDEN_PROMPT_SUFFIX_MARKER}${agentChatHistory}`
      : userText;

    try {
      const queryPayload: any = {
        agent_plan: finalAgentPlan,
        prompt_template_id: templateId,
        number_of_iterations: 5,
        grounding: true,
        // Add-in chat page (Deep mode): opt in to the interactive
        // surface so the resulting chat_history row shows up in the
        // chat with documents page's history list.
        source: "interactive_chat",
        previous_chat_history_id: messageChatId || undefined,
        title: userText,
      };

      if (includeTagIds && includeTagIds.length > 0) {
        queryPayload.include_tag_ids = includeTagIds;
      }
      if (excludeTagIds && excludeTagIds.length > 0) {
        queryPayload.exclude_tag_ids = excludeTagIds;
      }
      if (
        selectedReportTemplateId &&
        selectedReportTemplateId !== "default" &&
        selectedReportTemplateId.trim() !== ""
      ) {
        queryPayload.report_template_id = selectedReportTemplateId;
      }

      const result = await startAgentQuery(selectedProjectId, queryPayload);

      if (!result.success || !result.task_id) {
        throw new Error(result.error || result.message || "Failed to start agent analysis");
      }

      const taskId = result.task_id;

      setMessages((prev) =>
        prev.map((m) => (m.id === placeholderId ? { ...m, taskId } : m))
      );

      // Start SSE Streaming
      const controller = streamAgentTaskStatus(
        taskId,
        // onMessage:
        (taskData: any) => {
          const raw = taskData.data || taskData;
          const streamEvent = (raw.event || "").toLowerCase();

          // 1. Guard against stream_end event (prevents phantom RUNNING log)
          if (streamEvent === "stream_end") {
            Logger.debug("ChatPage: Agent SSE stream_end received");
            setIsLoading(false);
            setMessages((prev) =>
              prev.map((msg) => {
                if (msg.id !== placeholderId) return msg;
                const finalContent = msg.content?.trim() ? msg.content : EMPTY_ANSWER;
                return {
                  ...msg,
                  content: finalContent,
                  taskId,
                  isStreaming: false,
                  status: msg.status === "failed" ? "failed" : "completed",
                };
              })
            );
            return;
          }

          // 2. Ignore empty packets
          if (!raw.status && !raw.message && !raw.answer && !raw.response_data && !raw.result) {
            return;
          }

          const rawStatus = (raw.status || "").toLowerCase();
          const messageText = raw.message || "";
          let answer = raw.answer || raw.response_data?.answer;
          if (!answer && raw.result) {
            answer = raw.result.answer;
          }

          const sources = (raw.response_data?.sources || raw.result?.sources)?.map((s: any) => ({
            document_id: s.file_id || "",
            document: s.filename || "",
            content: s.content || "",
            file_info: {},
          }));

          const isTerminal =
            ["completed", "failed", "cancelled"].includes(rawStatus) || Boolean(answer);
          const finalStatus = isTerminal
            ? rawStatus === "failed"
              ? "failed"
              : "completed"
            : rawStatus || "running";
          const isStreamingNow = !isTerminal;

          // Per-message patch (O(N) once, not O(N)
          // per chunk). `appendLogToMessage` de-dupes identical
          // consecutive ticks so a flood of "Running..." events does
          // not grow the array on every chunk.
          setMessages((prev) => {
            const afterLog = messageText && messageText.trim() !== ""
              ? appendLogToMessage(prev, placeholderId, {
                  status: finalStatus,
                  message: messageText,
                  progress: raw.progress,
                  timestamp: Date.now(),
                })
              : prev;
            return patchMessageById(afterLog, placeholderId, {
              status: finalStatus,
              progress: raw.progress ?? undefined,
              content: answer ? (answer.trim() ? answer : EMPTY_ANSWER) : undefined,
              sources: sources || undefined,
              taskId,
              isStreaming: isStreamingNow,
              error: finalStatus === "failed" ? raw.error || "Agent task failed" : undefined,
            });
          });

          if (isTerminal) {
            setIsLoading(false);
            if (getAgentTaskStatus) {
              getAgentTaskStatus(taskId)
                .then((statusRes: any) => {
                  const meta = statusRes?.task_metadata;
                  if (meta?.chat_history_id) {
                    setMessageChatId(meta.chat_history_id);
                  }
                })
                .catch(() => {});
            }
          }
        },
        // onError:
        (err) => {
          Logger.error("ChatPage: Agent SSE Error:", err);
          if (getAgentTaskStatus) {
            getAgentTaskStatus(taskId)
              .then((statusRes) => {
                if (statusRes.answer) {
                  const finalContent = statusRes.answer.trim() ? statusRes.answer : EMPTY_ANSWER;
                  setMessages((prev) =>
                    prev.map((m) =>
                      m.id === placeholderId
                        ? { ...m, content: finalContent, taskId, isStreaming: false, status: "completed" }
                        : m
                    )
                  );
                }
              })
              .catch(() => {});
          }
          setIsLoading(false);
          setMessages((prev) =>
            prev.map((m) =>
              m.id === placeholderId
                ? {
                    ...m,
                    isStreaming: false,
                    status: m.content ? "completed" : "failed",
                    error: m.content ? undefined : "Connection to agent interrupted.",
                  }
                : m
            )
          );
        },
        // onClose:
        () => {
          setIsLoading(false);
          setMessages((prev) =>
            prev.map((m) =>
              m.id === placeholderId
                ? {
                    ...m,
                    isStreaming: false,
                    status: m.status === "failed" ? "failed" : "completed",
                  }
                : m
            )
          );
        }
      );

      activeStreamControllerRef.current = controller;
    } catch (err: any) {
      Logger.error("ChatPage: Failed Deep Thinking start:", err);
      const errorDetail =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        err?.message ||
        "Unable to complete deep thinking analysis. Please try again.";
      const displayMsg =
        typeof errorDetail === "string" ? errorDetail : JSON.stringify(errorDetail);

      setMessages((prev) =>
        prev.map((m) =>
          m.id === placeholderId
            ? {
                ...m,
                content: displayMsg,
                isStreaming: false,
                status: "failed",
                error: displayMsg,
              }
            : m
        )
      );
      setIsLoading(false);
    }
  };

  // ────────────────────────────────────────────
  // Past Chat Management Handlers
  // ────────────────────────────────────────────

  const handleOpenPastChat = async (item: ChatHistory) => {
    if (!getChatThread) return;
    try {
      const data = await getChatThread(item.id);
      const thread: ChatHistory[] = data.thread || [];

      const reopened = chatThreadToMessages(thread);

      setMessages(reopened.length ? reopened : defaultChatMessage);
      if (setMessageChatId) setMessageChatId(item.id);
      setShowHistoryDrawer(false);
    } catch {
      antdMessage.error("Could not load past conversation");
    }
  };

  const handlePinChat = async (item: ChatHistory) => {
    if (!setChatPinned) return;
    await setChatPinned(item.id, !item.is_pinned);
  };

  const handleDeleteChat = async (item: ChatHistory) => {
    if (!deleteChatHistory) return;
    await deleteChatHistory(item.id);
    // The deleted chat must never be restored on the next taskpane load.
    if (organizationId && selectedProjectId) {
      const storedId = getStoredLastChatId(organizationId, selectedProjectId);
      if (storedId === item.id) {
        clearStoredLastChatId(organizationId, selectedProjectId);
      }
    }
    if (messageChatId === item.id) {
      handleNewChatClick();
    }
    antdMessage.success("Chat deleted");
  };

  const handleRenameChat = async (id: string, title: string) => {
    if (!renameChatHistory) return;
    await renameChatHistory(id, title);
    antdMessage.success("Chat renamed");
  };

  const handleNewChatClick = () => {
    if (isLoading) return;
    if (activeStreamControllerRef.current) {
      activeStreamControllerRef.current.abort();
      activeStreamControllerRef.current = null;
    }
    // Starting fresh explicitly drops the "last chat" pointer so the old
    // conversation is not restored on the next reload.
    if (organizationId && selectedProjectId) {
      clearStoredLastChatId(organizationId, selectedProjectId);
    }
    if (onNewChat) {
      onNewChat();
    } else {
      setMessages(defaultChatMessage);
      if (setMessageChatId) setMessageChatId(null);
    }
    setInputValue("");
    setShowHistoryDrawer(false);
  };

  // ────────────────────────────────────────────
  // Last Chat Session Restore (per org + project)
  // ────────────────────────────────────────────

  // Remember the active chat id so a taskpane reload can pick up where the
  // user left off. Scoped by (organizationId, projectId).
  useEffect(() => {
    if (!organizationId || !selectedProjectId || !messageChatId) return;
    storeLastChatId(organizationId, selectedProjectId, messageChatId);
  }, [organizationId, selectedProjectId, messageChatId]);

  // Restore the stored last chat once per (org, project) scope, but only when
  // the page is in its pristine state (welcome message, no active thread).
  const restoredScopeRef = useRef<string | null>(null);
  const isPristineChat =
    messages.length === 1 && messages[0]?.id === "welcome" && !messageChatId;

  useEffect(() => {
    if (!organizationId || !selectedProjectId || !getChatThread) return undefined;

    const scope = `${organizationId}:${selectedProjectId}`;
    if (restoredScopeRef.current === scope) return undefined;
    restoredScopeRef.current = scope;

    if (!isPristineChat) return undefined;

    const storedId = getStoredLastChatId(organizationId, selectedProjectId);
    if (!storedId) return undefined;

    let cancelled = false;
    getChatThread(storedId)
      .then((data: any) => {
        if (cancelled) return;
        // Validate the thread still belongs to the current project (and
        // therefore the current organization); otherwise silently drop it.
        if (data?.project_id && selectedProjectId && data.project_id !== selectedProjectId) {
          clearStoredLastChatId(organizationId, selectedProjectId);
          return;
        }
        const thread: ChatHistory[] = data.thread || [];
        const reopened = chatThreadToMessages(thread);
        if (reopened.length > 0) {
          setMessages(reopened);
          if (setMessageChatId) setMessageChatId(storedId);
        } else {
          clearStoredLastChatId(organizationId, selectedProjectId);
        }
      })
      .catch(() => {
        // 404 / network error: silently forget the stored pointer.
        if (!cancelled) clearStoredLastChatId(organizationId, selectedProjectId);
      });

    return () => {
      cancelled = true;
    };
    // isPristineChat is intentionally read once per scope change; message
    // updates after restore must not re-trigger the fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId, selectedProjectId, getChatThread]);

  // ────────────────────────────────────────────
  // Turn Editing & Clipboard
  // ────────────────────────────────────────────

  function navigator_clipboard_writeText(text: string): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        const textArea = document.createElement("textarea") as HTMLTextAreaElement;
        textArea.value = text;
        document.body.appendChild(textArea);
        textArea.select();
        const successful = document.execCommand("copy");
        document.body.removeChild(textArea);
        if (successful) resolve();
        else reject(new Error("execCommand failed"));
      } catch (e) {
        reject(e);
      }
    });
  }

  const copyToClipboard = async (content: string, messageId: string) => {
    try {
      if (
        Office.context.diagnostics.platform !== Office.PlatformType.OfficeOnline &&
        navigator.clipboard?.writeText
      ) {
        await navigator.clipboard.writeText(content);
      } else {
        await navigator_clipboard_writeText(content);
      }
      setCopiedMessageId(messageId);
      setTimeout(() => setCopiedMessageId(null), 2000);
    } catch {
      antdMessage.error("Failed to copy");
    }
  };

  const startEditing = (messageId: string, content: string) => {
    setEditingMessageId(messageId);
    setEditingContent(content);
  };

  const cancelEditing = () => {
    setEditingMessageId(null);
    setEditingContent("");
  };

  const saveEdit = async () => {
    if (!editingContent.trim() || isLoading) return;
    const editedText = editingContent.trim();
    const editIndex = messages.findIndex((m) => m.id === editingMessageId);
    if (editIndex === -1) return;

    const updated = messages.slice(0, editIndex);
    setMessages(updated);
    cancelEditing();

    setInputValue(editedText);
    setTimeout(() => {
      handleSendMessage();
    }, 50);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // ────────────────────────────────────────────
  // Conversational Voice Mode (listening + spoken replies)
  // ────────────────────────────────────────────

  const lastAssistantMessageText = useMemo(
    () => [...messages].reverse().find((m) => m.role === "assistant" && !m.error && m.content)?.content ?? "",
    [messages]
  );

  const voiceMode = useVoiceChatMode({
    transcript: inputValue,
    onTranscriptChange: setInputValue,
    onSubmit: (text) => handleSendMessage(text),
    isProcessing: isLoading,
    lastAssistantMessage: lastAssistantMessageText,
    transcribeAudio,
    synthesizeSpeech,
  });

  const focusComposerAtEnd = () => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.focus({ preventScroll: true });
    const end = textarea.value.length;
    textarea.setSelectionRange(end, end);
  };

  // Dictation and voice mode never hold the microphone at the same time.
  const dictation = useVoiceDictation({
    value: inputValue,
    onChange: setInputValue,
    transcribeAudio,
    disabled: !active || isLoading || voiceMode.isActive,
    onListeningStart: focusComposerAtEnd,
  });

  const handleVoiceModeToggle = () => {
    dictation.cancel();
    voiceMode.toggle();
  };

  const dictationStatusLabel =
    dictation.status === "listening"
      ? dictation.activation === "hold"
        ? "Listening… release Ctrl to finish (Esc to cancel)"
        : dictation.activation === "tap"
        ? "Listening… tap Ctrl to finish (Esc to cancel)"
        : "Listening… click the mic to finish (Esc to cancel)"
      : dictation.status === "transcribing"
      ? "Transcribing your voice…"
      : null;

  const voiceStatusLabel =
    voiceMode.status === "listening"
      ? "Listening… (Esc to stop)"
      : voiceMode.status === "transcribing"
      ? "Transcribing your voice…"
      : voiceMode.status === "submitting"
      ? "Thinking…"
      : voiceMode.status === "speaking"
      ? "Jenly is speaking… (Esc to stop)"
      : null;

  const displayVoiceText = inputValue.trim();

  const adjustTextareaHeight = () => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
    }
  };

  const handleInputWrapperClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // Do not interfere if user clicked an action button or an element inside one
    if ((e.target as HTMLElement).closest("button, [role='button']")) {
      return;
    }

    if (textareaRef.current && !isLoading && !voiceMode.isActive) {
      textareaRef.current.focus();
      if (e.target !== textareaRef.current) {
        const len = textareaRef.current.value.length;
        textareaRef.current.setSelectionRange(len, len);
      }
    }
  };

  useEffect(() => {
    adjustTextareaHeight();
  }, [inputValue]);

  const formatTime = (ts: Date | string | undefined) => {
    if (!ts) return "";
    const d = typeof ts === "string" ? new Date(ts) : ts;
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  return (
    <div className="llm-chat-container">
      {/* ── Chat Header Bar ── */}
      <div className="chat-header-bar">
        <ChatModeToggle thinkMode={thinkMode} onChange={setThinkMode} disabled={isLoading} />

        <div className="chat-header-actions">
          {isSuperAdmin && (
            <>
              <ChatModelSelector
                models={models}
                selectedModel={selectedModel}
                onSelect={setSelectedModel}
                visible={showModelMenu}
                onToggle={() => {
                  setShowModelMenu((v) => !v);
                  setShowTagMenu(false);
                }}
                onClose={() => setShowModelMenu(false)}
              />
              {tags.length > 0 && (
                <ChatTagSelector
                  tags={tags}
                  includeTagIds={includeTagIds}
                  excludeTagIds={excludeTagIds}
                  onToggleTag={handleToggleTag}
                  visible={showTagMenu}
                  onToggle={() => {
                    setShowTagMenu((v) => !v);
                    setShowModelMenu(false);
                  }}
                  onClose={() => setShowTagMenu(false)}
                />
              )}
            </>
          )}

          <Tooltip title="Past Chats" placement="bottom">
            <button
              type="button"
              onClick={() => setShowHistoryDrawer(true)}
              className="chat-header-btn"
              aria-label="Past Chats"
            >
              <HistoryOutlined style={{ fontSize: 14 }} />
            </button>
          </Tooltip>

          <Tooltip title="New Chat" placement="bottom">
            <button
              type="button"
              onClick={handleNewChatClick}
              className="chat-header-btn"
              aria-label="New Chat"
            >
              <SquarePen size={14} />
            </button>
          </Tooltip>
        </div>
      </div>

      {/* ── Messages List Area ── */}
      <div className="llm-chat-messages">
        {messages.map((message) => {
          const isUser = message.role === "user";

          return (
            <div key={message.id} className={`llm-chat-message ${isUser ? "user" : "assistant"}`}>
              {!isUser && (
                <div className="llm-chat-assistant-avatar" aria-hidden="true">
                  <img src="/assets/brand-icon.svg" alt="" />
                </div>
              )}

              <div className="llm-chat-message-content">
                {editingMessageId === message.id ? (
                  <div className="llm-chat-edit-container">
                    <textarea
                      value={editingContent}
                      onChange={(e) => setEditingContent(e.target.value)}
                      className="llm-chat-edit-textarea"
                      autoFocus
                    />
                    <div className="llm-chat-edit-actions">
                      <button onClick={saveEdit} className="llm-chat-edit-save" disabled={!editingContent.trim()}>
                        <CheckOutlined /> Save
                      </button>
                      <button onClick={cancelEditing} className="llm-chat-edit-cancel">
                        <CloseOutlined /> Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="llm-chat-message-bubble-container">
                    {/* Deep Thinking Indicator for Agent Messages */}
                    {!isUser && message.logs !== undefined && (
                      <DeepThinkingIndicator
                        logs={message.logs}
                        status={message.status || (message.isStreaming ? "running" : "completed")}
                        progress={message.progress}
                        isStreaming={message.isStreaming}
                      />
                    )}

                    {/* Message Bubble: in Deep Thinking mode, only show once there is actual output or an error */}
                    {(!isUser && message.logs !== undefined
                      ? Boolean(message.content || message.error)
                      : Boolean(message.content || message.isStreaming || message.error)
                    ) && (
                      <div className={`llm-chat-message-bubble ${message.error ? "error" : ""}`}>
                        {message.content ? (
                          <ReactMarkdown
                            remarkPlugins={[remarkGfm]}
                            components={
                              !isUser && message.taskId
                                ? getGroundingMarkdownComponents({
                                    onGroundingLinkClick: (kind, id) => {
                                      handleOpenGrounding(message.taskId, { kind, id });
                                    },
                                    openExternalLinksInNewTab: true,
                                  })
                                : undefined
                            }
                          >
                            {!isUser && message.taskId
                              ? preprocessGroundingMarkerRuns(message.content)
                              : message.content}
                          </ReactMarkdown>
                        ) : message.isStreaming ? (
                          <div className="llm-chat-typing-indicator">
                            <div className="llm-chat-typing-dot" />
                            <div className="llm-chat-typing-dot" />
                            <div className="llm-chat-typing-dot" />
                          </div>
                        ) : null}
                      </div>
                    )}

                    <div className="llm-chat-message-actions">
                      <span className="llm-chat-message-time">{formatTime(message.timestamp)}</span>
                      {!isUser && message.taskId && (
                        <button
                          type="button"
                          onClick={() => handleOpenGrounding(message.taskId)}
                          className="llm-chat-evidence-action"
                          title="View Grounding Evidence"
                          aria-label="View Grounding Evidence"
                        >
                          <Sparkles size={11} />
                          <span>Evidence</span>
                        </button>
                      )}
                      {location !== "online" && message.content && (
                        <button
                          onClick={() => copyToClipboard(message.content, message.id)}
                          className="llm-chat-message-action"
                          title="Copy message"
                        >
                          {copiedMessageId === message.id ? <CheckOutlined /> : <CopyOutlined />}
                        </button>
                      )}
                      {isUser && (
                        <button
                          onClick={() => startEditing(message.id, message.content)}
                          className="llm-chat-message-action"
                          title="Edit message"
                        >
                          <EditOutlined />
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {isLoading && thinkMode === "lite" && (
          <div className="llm-chat-message assistant loading">
            <div className="llm-chat-assistant-avatar flapping" aria-hidden="true">
              <img src="/assets/brand-loader.svg" alt="" />
            </div>
            <div className="llm-chat-message-content">
              <div className="llm-chat-message-bubble">
                <div className="llm-chat-typing-indicator">
                  <div className="llm-chat-typing-dot" />
                  <div className="llm-chat-typing-dot" />
                  <div className="llm-chat-typing-dot" />
                </div>
                <LongRunningIndicator active />
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* ── Input Area ── */}
      <div className="llm-chat-input-container">
        {/* Ambient Flame Glow blooming softly above the composer in Voice Mode (Signature Brand Cyan/Sky Blue) */}
        {voiceMode.isActive && (
          <div aria-hidden="true" className="llm-chat-voice-flame-container">
            <div
              className="llm-chat-voice-flame-bloom"
              style={{
                transform: `scale(${1 + (voiceMode.status === "listening" ? voiceMode.audioLevel * 0.08 : 0.03)})`,
                opacity: voiceMode.status === "speaking" ? 0.50 : 0.35 + voiceMode.audioLevel * 0.18,
              }}
            />
            <div
              className="llm-chat-voice-flame-body"
              style={{
                transform: `scale(${1 + (voiceMode.status === "listening" ? voiceMode.audioLevel * 0.06 : 0.02)})`,
                opacity: voiceMode.status === "speaking" ? 0.45 : 0.32 + voiceMode.audioLevel * 0.15,
              }}
            />
            <div
              className="llm-chat-voice-flame-horizon"
              style={{
                transform: `scale(${1 + (voiceMode.status === "listening" ? voiceMode.audioLevel * 0.05 : 0.02)})`,
                opacity: voiceMode.status === "speaking" ? 0.48 : 0.35 + voiceMode.audioLevel * 0.14,
              }}
            />
          </div>
        )}

        <AIProcessingButtonFrame
          active={isLoading}
          className="llm-chat-input-processing-frame"
          width="100%"
          targetSelector=".llm-chat-input-wrapper"
        >
          <div
            className={`llm-chat-input-wrapper ${voiceMode.isActive ? "voice-mode-active" : ""} ${
              dictation.status === "listening" ? "dictation-active" : ""
            }`}
            onClick={handleInputWrapperClick}
          >
            {voiceMode.isActive ? (
              <div className="llm-chat-voice-capsule-body">
                <div className="llm-chat-voice-capsule-content">
                  {displayVoiceText ? (
                    <p className="llm-chat-voice-capsule-quote">
                      &ldquo;{displayVoiceText}&rdquo;
                    </p>
                  ) : voiceMode.status === "transcribing" ? (
                    <div className="llm-chat-voice-capsule-status">
                      <LoadingOutlined spin style={{ fontSize: "0.85rem", color: "#00b4d8" }} />
                      <span>Transcribing your voice…</span>
                    </div>
                  ) : voiceMode.status === "submitting" || isLoading ? (
                    <div className="llm-chat-voice-capsule-status">
                      <LoadingOutlined spin style={{ fontSize: "0.85rem", color: "#00b4d8" }} />
                      <span>Thinking…</span>
                    </div>
                  ) : voiceMode.status === "speaking" ? (
                    <div className="llm-chat-voice-capsule-status">
                      <span className="llm-chat-bouncing-dots" aria-hidden="true">
                        <span className="llm-chat-bouncing-dot" />
                        <span className="llm-chat-bouncing-dot" />
                        <span className="llm-chat-bouncing-dot" />
                      </span>
                      <span>Jenly is speaking…</span>
                    </div>
                  ) : (
                    <p className="llm-chat-voice-capsule-listening">Listening…</p>
                  )}
                </div>
                <button
                  type="button"
                  className="llm-chat-voice-capsule-stop-button"
                  onClick={(e) => {
                    e.stopPropagation();
                    voiceMode.exit();
                  }}
                  title="Stop voice mode (Esc)"
                  aria-label="Stop voice mode"
                >
                  <Square size={11} fill="currentColor" />
                  <span>Stop</span>
                </button>
              </div>
            ) : null}

            <textarea
              ref={textareaRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={dictationStatusLabel ?? activePlaceholder}
              className="llm-chat-input"
              rows={1}
              disabled={!selectedProjectId || isLoading}
              style={voiceMode.isActive ? { display: "none" } : undefined}
            />

            <div className="llm-chat-input-actions-row">
              <div className="llm-chat-input-actions-left" />
              <div className="llm-chat-input-actions-right">
                {inputValue.trim() && !isLoading && !voiceMode.isActive && selectedProjectId && (
                  <Button onClick={() => setInputValue("")} className="llm-chat-clear-button">
                    <CloseCircleOutlined style={{ fontSize: "0.75rem" }} />
                  </Button>
                )}

                <Tooltip title="Advanced AI Chat">
                  <Button
                    onClick={() => advancedAIChatUrl && window.open(advancedAIChatUrl, "_blank")}
                    disabled={!advancedAIChatUrl || voiceMode.isActive || !selectedProjectId}
                    className="llm-chat-advanced-button"
                    aria-label="Advanced AI Chat"
                  >
                    <WandSparkles size={15} strokeWidth={2} />
                  </Button>
                </Tooltip>

                <Tooltip
                  title={
                    !selectedProjectId
                      ? "Select a project first"
                      : !voiceMode.isSupported
                      ? "Voice mode is not supported in this browser"
                      : voiceMode.isActive
                      ? "Stop voice mode (Esc)"
                      : "Voice conversation: talk and Jenly answers aloud"
                  }
                >
                  <button
                    type="button"
                    onClick={handleVoiceModeToggle}
                    disabled={!selectedProjectId || !voiceMode.isSupported}
                    className={`llm-chat-voice-mode-button ${voiceMode.isActive ? "active" : ""}`}
                    aria-label={voiceMode.isActive ? "Voice conversation active" : "Start voice mode"}
                    aria-pressed={voiceMode.isActive}
                  >
                    <span className="llm-chat-waveform-bars" aria-hidden="true">
                      <span className="llm-chat-waveform-bar" />
                      <span className="llm-chat-waveform-bar" />
                      <span className="llm-chat-waveform-bar" />
                      <span className="llm-chat-waveform-bar" />
                      <span className="llm-chat-waveform-bar" />
                    </span>
                  </button>
                </Tooltip>

                <Tooltip
                  title={
                    !selectedProjectId
                      ? "Select a project first"
                      : !dictation.isSupported
                      ? "Dictation is not supported in this browser"
                      : dictation.status === "listening"
                      ? "Stop dictating"
                      : dictation.status === "transcribing"
                      ? "Transcribing…"
                      : "Dictate: click to speak and transcribe"
                  }
                >
                  <button
                    type="button"
                    onClick={dictation.toggle}
                    disabled={
                      !selectedProjectId ||
                      !dictation.isSupported ||
                      isLoading ||
                      voiceMode.isActive ||
                      dictation.status === "transcribing"
                    }
                    className={`llm-chat-voice-mode-button ${
                      dictation.status === "listening" ? "active" : ""
                    }`}
                    aria-label={dictation.status === "listening" ? "Stop dictating" : "Start dictating"}
                    aria-pressed={dictation.status === "listening"}
                  >
                    {dictation.status === "transcribing" ? (
                      <LoadingOutlined spin style={{ fontSize: "0.9rem" }} />
                    ) : (
                      <Mic size={15} strokeWidth={2} />
                    )}
                    {dictation.status === "listening" && (
                      <span className="llm-chat-voice-pulse" aria-hidden="true" />
                    )}
                  </button>
                </Tooltip>

                <Button
                  onClick={() => handleSendMessage()}
                  disabled={!selectedProjectId || !inputValue.trim() || isLoading || voiceMode.isActive}
                  className="llm-chat-send-button"
                >
                  {isLoading ? (
                    <LoadingOutlined spin style={{ fontSize: "1rem" }} />
                  ) : (
                    <SendOutlined style={{ fontSize: "0.9rem" }} />
                  )}
                </Button>
              </div>
            </div>
          </div>
        </AIProcessingButtonFrame>
        {voiceMode.isActive ? null : dictation.status !== "idle" ? (
          <div className="llm-chat-voice-status" role="status">
            {dictation.status === "listening" ? (
              <VoiceLevelBars level={dictation.audioLevel} />
            ) : (
              <span className="llm-chat-voice-dot" aria-hidden="true" />
            )}
            <span className="llm-chat-voice-status-text">{dictationStatusLabel}</span>
          </div>
        ) : voiceMode.error || dictation.error ? (
          <div className="llm-chat-voice-status error" role="alert">
            {voiceMode.error || dictation.error}
          </div>
        ) : (
          <div className="llm-chat-input-hint">
            Press Enter to send, Shift+Enter for new line · Hold Ctrl to talk, tap Ctrl for hands-free
          </div>
        )}
      </div>

      {/* ── Grounding Evidence Drawer ── */}
      <ChatGroundingDrawer
        visible={Boolean(groundingDrawerTaskId)}
        onClose={() => {
          setGroundingDrawerTaskId(null);
          setGroundingDrawerTarget(null);
        }}
        taskId={groundingDrawerTaskId}
        target={groundingDrawerTarget}
        retrieveAgentGrounding={retrieveAgentGrounding}
        retrieveSourceDocument={retrieveSourceDocument}
        projectId={selectedProjectId}
      />

      {/* ── Past Chats Slide-Over Drawer Overlay ── */}
      <ChatHistoryDrawer
        visible={showHistoryDrawer}
        onClose={() => setShowHistoryDrawer(false)}
        onNewChat={handleNewChatClick}
        onOpenChat={handleOpenPastChat}
        onPinChat={handlePinChat}
        onDeleteChat={handleDeleteChat}
        onRenameChat={handleRenameChat}
        projectId={selectedProjectId || null}
        getChatHistory={getProjectChatHistory!}
        activeChatId={messageChatId || null}
      />
    </div>
  );
};

export default ChatPage;
